import { FC, useMemo } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { useHasAccess } from '../../../../hooks/auth'
import { useEstoqueHoje } from '../../../../hooks/estoque'
import { ESTOQUE_PERM, HojeContadores } from '../../../../types/estoque'
import { OS_PERM } from '../../../../types/os'
import { PREVISOES_PERM } from '../../../../types/previsoes'
import { GrupoMenu, ItemMenu, gruposDoMais } from '../../menu'
import { IconeDoMenu } from '../../menuIcones'
import { BD, Card, PageSubtitle, PageTitle } from '../../styles'

const Grupos = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TituloGrupo = styled.h2`
  margin: 0 0 6px 2px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${BD.grayText};
`

const Lista = styled(Card)`
  padding: 0 14px;
`

const Linha = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  color: ${BD.black};
  font-size: 15px;
  text-decoration: none;
  & + & {
    border-top: 1px solid ${BD.grayBorder};
  }
  span.rotulo {
    flex-grow: 1;
  }
`

const Contador = styled.span`
  min-width: 24px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: ${BD.redLight};
  color: ${BD.redDark};
  font-size: 12px;
  font-weight: 600;
  text-align: center;
`

const Seta: FC = () => (
  <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke={BD.grayText} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m9 18 6-6-6-6" />
  </svg>
)

const BADGE: Record<string, (c: HojeContadores) => number> = {
  '/interno/estoque/transferencias': (c) =>
    Number(c.transferenciasAguardandoAprovacao || 0)
    + Number(c.transferenciasAprovadas || 0)
    + Number(c.transferenciasEmSeparacao || 0)
    + Number(c.transferenciasAguardandoConferencia || 0),
  '/interno/estoque/baixas': (c) => Number(c.baixasPendentes || 0),
  '/interno/estoque/ocorrencias': (c) => Number(c.ocorrenciasAbertas || 0),
  '/interno/estoque/inventarios': (c) =>
    Number(c.inventariosEmContagem || 0) + Number(c.inventariosAguardandoAprovacao || 0),
}

/**
 * OP-3619 — o resto do menu, no celular.
 *
 * A barra de abas leva as quatro telas do dia; tudo mais que a pessoa pode
 * abrir mora aqui, nos mesmos grupos do menu lateral e com o que espera
 * decisão já contado ao lado.
 */
const Conteudo: FC<{ grupos: GrupoMenu[]; contadores: HojeContadores | null }> = ({ grupos, contadores }) => {
  const numero = (item: ItemMenu): number => {
    const conta = BADGE[item.to]
    return contadores && conta ? conta(contadores) : 0
  }

  return (
    <>
      <PageTitle>Mais</PageTitle>
      <PageSubtitle>O que não coube na barra de abas. Os números respeitam a sua praça.</PageSubtitle>
      <Grupos>
        {grupos.map((g) => (
          <section key={g.id}>
            <TituloGrupo>{g.titulo}</TituloGrupo>
            <Lista>
              {g.itens.map((item) => {
                const total = numero(item)
                return (
                  <Linha key={item.to} to={item.to}>
                    <IconeDoMenu nome={item.icone} tamanho={20} />
                    <span className="rotulo">{item.rotulo}</span>
                    {total > 0 && <Contador>{total}</Contador>}
                    <Seta />
                  </Linha>
                )
              })}
            </Lista>
          </section>
        ))}
      </Grupos>
    </>
  )
}

const ConteudoComContadores: FC<{ grupos: GrupoMenu[] }> = ({ grupos }) => {
  const { data } = useEstoqueHoje()
  return <Conteudo grupos={grupos} contadores={data?.contadores ?? null} />
}

/**
 * OP-3619 — o resto do menu, no celular.
 *
 * A barra de abas leva as quatro telas do dia; tudo mais que a pessoa pode
 * abrir mora aqui, nos mesmos grupos do menu lateral e com o que espera
 * decisão já contado ao lado.
 */
export const EstoqueMais: FC = () => {
  const canImport = useHasAccess(OS_PERM.IMPORTAR)
  const canAnalytics = useHasAccess(OS_PERM.ANALYTICS)
  const canTecnicos = useHasAccess(OS_PERM.GERENCIAR_TECNICOS)
  const canConfig = useHasAccess(OS_PERM.CONFIGURAR)
  const canEstoque = useHasAccess(ESTOQUE_PERM.VISUALIZAR)
  const canSolicitar = useHasAccess(ESTOQUE_PERM.SOLICITAR)
  const canPatrimonio = useHasAccess(ESTOQUE_PERM.PATRIMONIO)
  const canNotificacoes = useHasAccess(ESTOQUE_PERM.NOTIFICACOES)
  const canPrevisoes = useHasAccess(PREVISOES_PERM.VISUALIZAR)

  const grupos = useMemo(
    () => gruposDoMais({
      importacoes: canImport,
      analytics: canAnalytics,
      tecnicos: canTecnicos,
      config: canConfig,
      estoque: canEstoque,
      solicitar: canSolicitar,
      patrimonio: canPatrimonio,
      notificacoes: canNotificacoes,
      previsoes: canPrevisoes,
    }),
    [canImport, canAnalytics, canTecnicos, canConfig, canEstoque, canSolicitar, canPatrimonio, canNotificacoes, canPrevisoes],
  )

  if (canEstoque) return <ConteudoComContadores grupos={grupos} />
  return <Conteudo grupos={grupos} contadores={null} />
}

export default EstoqueMais
